import { useState, useMemo, useEffect } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import { useUserCustomization } from '../../../../hooks/useUserCustomization';

/**
 * useBaseDiscoveryTable - A shared hook for Discovery table logic
 * Used by both Inventory and MoveGroupsAndApps
 * takes tableType = 'inventory_table' or 'move_groups_applications_table'
 */
export const useBaseDiscoveryTable = ({ tableData, columns, selectedColumns, tableType = 'inventory_table' }) => {
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);

    // Get user customization from hook (session storage + API)
    const { getTableCustomization, updateColumnOrder, isLoading: customizationLoading } = useUserCustomization();
    const customization = getTableCustomization(tableType);

    // Initialize column order from customization or default to all columns
    const [columnOrder, setColumnOrder] = useState(() => {
        const allColumnKeys = columns.map((col) => col.key);
        if (customization?.columns && customization.columns.length > 0) {
            // Merge saved customization with all available columns
            const savedKeys = new Set(customization.columns);
            const newKeys = allColumnKeys.filter((key) => !savedKeys.has(key));
            return [...customization.columns, ...newKeys];
        }
        return allColumnKeys;
    });

    // Update column order when customization changes (from API load)
    useEffect(() => {
        if (customization?.columns && customization.columns.length > 0 && !customizationLoading) {
            const allColumnKeys = columns.map((col) => col.key);
            // Merge saved customization with all available columns
            const savedKeys = new Set(customization.columns);
            const newKeys = allColumnKeys.filter((key) => !savedKeys.has(key));
            setColumnOrder([...customization.columns, ...newKeys]);
        }
    }, [customization, customizationLoading, columns]);

    const data = tableData;

    // Clear selected rows when table data changes. When paginating, filtering, or searching
    useEffect(() => {
        setSelectedRowKeys([]);
        setSelectedRows([]);
    }, [tableData]);

    const onSelectChange = (newSelectedRowKeys, newSelectedRows) => {
        setSelectedRowKeys(newSelectedRowKeys);
        setSelectedRows(newSelectedRows);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        getCheckboxProps: (record) => ({
            name: record.key,
        }),
    };

    const visibleColumns = useMemo(() => {
        return columns.filter((column) => {
            // Always show selection, status, and actions columns
            if (column.key === 'actions' || column.key === 'ready_status' || column.key === 'selection') {
                return true;
            }
            return selectedColumns.includes(column.title);
        });
    }, [columns, selectedColumns]);

    const orderedColumns = useMemo(() => {
        const columnMap = new Map(visibleColumns.map((col) => [col.key, col]));

        // Filter columnOrder to only include visible columns and map to column objects
        // Exclude actions and status columns from ordering
        const ordered = columnOrder
            .filter((key) => columnMap.has(key) && key !== 'actions' && key !== 'ready_status')
            .map((key) => {
                const col = columnMap.get(key);
                // Apply sorting customization if it matches this column
                if (customization?.sorting && customization.sorting.field === key) {
                    return {
                        ...col,
                        sortOrder: customization.sorting.direction,
                    };
                }
                return col;
            });

        // Add status column at the beginning
        const statusColumn = visibleColumns.find((col) => col.key === 'ready_status');
        if (statusColumn) {
            ordered.unshift(statusColumn);
        }

        const actionsColumn = visibleColumns.find((col) => col.key === 'actions');
        if (actionsColumn) {
            ordered.push(actionsColumn);
        }

        return ordered;
    }, [columnOrder, visibleColumns, customization?.sorting]);

    const handleDragEnd = (event) => {
        const { active, over } = event;

        // Prevent dragging the actions and status columns
        if (
            active.id === 'actions' ||
            over?.id === 'actions' ||
            active.id === 'ready_status' ||
            over?.id === 'ready_status'
        ) {
            return;
        }

        if (over && active.id !== over.id) {
            setColumnOrder((items) => {
                const oldIndex = items.indexOf(active.id);
                const newIndex = items.indexOf(over.id);
                const newOrder = arrayMove(items, oldIndex, newIndex);

                // Save the new column order to customization, excluding actions and status
                const customizationOrder = newOrder.filter((key) => key !== 'actions' && key !== 'ready_status');
                updateColumnOrder(customizationOrder, tableType);

                return newOrder;
            });
        }
    };

    return {
        data,
        rowSelection,
        selectedRowKeys,
        selectedRows,
        visibleColumns: orderedColumns,
        columnOrder,
        handleDragEnd,
        customizationLoading,
    };
};
